// Redux Screenings module
// see https://github.com/erikras/ducks-modular-redux

import { change, getFormValues } from 'redux-form';
import { Screenings, Utils } from '../../services/';
import { make_temporary_pricing_id } from '../../services/entityIdFromName';
import { merge_pricings_for_form_sync } from '../../services/syncFormPricings';
import * as _ from 'es-toolkit';
import moment from 'moment';
import { v4 } from 'uuid';
import {
    persistScreeningsFiltersUi,
    readPersistedScreeningsFiltersUi,
    resolveScreeningsFiltersSession,
} from '../../services/kronosPrefs/screeningsFiltersUi';
import { createRestSlices, applyReducers } from './rest';

const { linkedResourcesReducer, linkedResourcesAction } = createRestSlices(Screenings);

const SCREENING_FORM = 'ScreeningForm';
const SCREENING_FORM_GENERAL = 'screeningFormGeneral';
const INTERNAL_SCREENING_BUCKET_IDS = ['overbooking', 'no-show'];

const sync_screening_form_pricings = (dispatch, getState, renamed) => {
    const reducer_pricings = getState().screenings?.screening?.pricings || {};
    const form_pricings = getState().form?.[SCREENING_FORM_GENERAL]?.values?.pricings || {};
    dispatch(change(
        SCREENING_FORM_GENERAL,
        'pricings',
        merge_pricings_for_form_sync(form_pricings, reducer_pricings, renamed)
    ));
};

const LOADING_SCREENINGS                  = 'kronos/screenings/LOADING_SCREENINGS';
const LOADING_SCREENINGS_SUCCESS          = 'kronos/screenings/LOADING_SCREENINGS_SUCCESS';
const LOADING_SCREENINGS_FAILURE          = 'kronos/screenings/LOADING_SCREENINGS_FAILURE';
const LOADING_SCREENING                   = 'kronos/screenings/LOADING_SCREENING';
const LOADING_SCREENING_SUCCESS           = 'kronos/screenings/LOADING_SCREENING_SUCCESS';
const LOADING_SCREENING_FAILURE           = 'kronos/screenings/LOADING_SCREENING_FAILURE';
const UPDATING_SCREENING                  = 'kronos/screenings/UPDATING_SCREENING';
const UPDATING_SCREENING_SUCCESS          = 'kronos/screenings/UPDATING_SCREENING_SUCCESS';
const UPDATING_SCREENING_FAILURE          = 'kronos/screenings/UPDATING_SCREENING_FAILURE';
const START_CREATING_SCREENING            = 'kronos/screenings/START_CREATING_SCREENING';
const CREATING_SCREENING                  = 'kronos/screenings/CREATING_SCREENING';
const CREATING_SCREENING_SUCCESS          = 'kronos/screenings/CREATING_SCREENING_SUCCESS';
const CREATING_SCREENING_FAILURE          = 'kronos/screenings/CREATING_SCREENING_FAILURE';
const DELETING_SCREENING                  = 'kronos/screenings/DELETING_SCREENING';
const DELETING_SCREENING_SUCCESS          = 'kronos/screenings/DELETING_SCREENING_SUCCESS';
const DELETING_SCREENING_FAILURE          = 'kronos/screenings/DELETING_SCREENING_FAILURE';
const ADDING_PRICING                      = 'kronos/screenings/ADDING_PRICING';
const DUPLICATING_PRICING                 = 'kronos/screenings/DUPLICATING_PRICING';
const REMOVING_PRICING                    = 'kronos/screenings/REMOVING_PRICING';
const TKTSYNCING_SCREENINGS               = 'kronos/screenings/TKTSYNCING_SCREENINGS';
const TKTSYNCING_SCREENINGS_SUCCESS       = 'kronos/screenings/TKTSYNCING_SCREENINGS_SUCCESS';
const TKTSYNCING_SCREENINGS_FAILURE       = 'kronos/screenings/TKTSYNCING_SCREENINGS_FAILURE';
const UPDATING_SCREENING_PROPERTY         = 'kronos/screenings/UPDATING_SCREENING_PROPERTY';
const UPDATING_SCREENING_PROPERTY_SUCCESS = 'kronos/screenings/UPDATING_SCREENING_PROPERTY_SUCCESS';
const UPDATING_SCREENING_PROPERTY_FAILURE = 'kronos/screenings/UPDATING_SCREENING_PROPERTY_FAILURE';
const UPDATING_SCREENING_PRICINGS         = 'kronos/tickettypes/UPDATING_TICKETTYPE_PRICINGS';
const UPDATING_SCREENING_PLACE            = 'kronos/tickettypes/UPDATING_TICKETTYPE_PLACE';
const UPDATING_FILTERS                    = 'kronos/screenings/UPDATING_FILTERS';
const UPDATING_PRICING_KEY                = 'kronos/pricinglists/UPDATING_PRICING_KEY';

const persistedScreeningsVenue = readPersistedScreeningsFiltersUi();

const initialState = {
    isLoading: false,
    listFetchScope: 'idle',
    loadingRequestId: null,
    hasLoadingError: false,
    loadingErrorMessage: '',
    screeningSuccessfullyCreated: false,
    screenings: [],
    screening: {},
    linkedResources: {},
    filters: {
        start_at_gte: moment().startOf('day'),
        places_ids: persistedScreeningsVenue.places_ids,
    },
    isTktsyncing: false,
    hasTktsyncingError: false,
    tktsyncingErrorMessage: '',
    imported: [],
}

const initialPricing = (names, currency, eshopWeight) => {
    return {
        name: names,
        // Empty strings keep Field paths bound without prefilling "0".
        price: { [currency]: '' },
        value: { [currency]: '' },
        value_per_screening: { [currency]: '' },
        wallet_amount: { [currency]: '' },
        variable_price: false,
        value_like_price: true,
        VAT: 0,
        rules: {
            only_for_roles: Utils.defaultPricingSellerRoles(),
        },
        opaque: {
            eshop_sort_weight: eshopWeight
        }
    }
}

// Reducer
function screeningsReducer(state = initialState, action) {
    switch (action.type) {
        case LOADING_SCREENINGS:
            return {
                ...state,
                isLoading: true,
                listFetchScope: action.fetchScope ?? 'major',
                loadingRequestId: action.requestId,
                hasLoadingError: false,
                screeningSuccessfullyDeleted: false
            };
        case LOADING_SCREENINGS_SUCCESS:
            if (state.loadingRequestId !== action.requestId)
                return state;
            return {
                ...state,
                isLoading: false,
                listFetchScope: 'idle',
                loadingRequestId: null,
                hasLoadingError: false,
                screenings: action.screenings
            };
        case LOADING_SCREENINGS_FAILURE:
            if (state.loadingRequestId !== action.requestId)
                return state;
            return {
                ...state,
                isLoading: false,
                listFetchScope: 'idle',
                loadingRequestId: null,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case LOADING_SCREENING:
            return {
                ...state,
                isLoading: true,
                screeningSuccessfullyDeleted: false,
                hasLoadingError: false
            };
        case LOADING_SCREENING_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                screening: action.screening
            };
        case LOADING_SCREENING_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case UPDATING_SCREENING:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case UPDATING_SCREENING_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                screening: action.screening
            };
        case UPDATING_SCREENING_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case START_CREATING_SCREENING:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                screeningSuccessfullyCreated: false,
                screening: {}
            };
        case CREATING_SCREENING:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case CREATING_SCREENING_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                screeningSuccessfullyCreated: true,
                screening: action.screening
            };
        case CREATING_SCREENING_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                screeningSuccessfullyCreated: false,
                loadingErrorMessage: action.error.message
            };
        case DELETING_SCREENING:
            return {
                ...state,
                isLoading: true,
                screeningSuccessfullyDeleted: false,
                hasLoadingError: false
            };
        case DELETING_SCREENING_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                screeningSuccessfullyDeleted: true,
                screening: action.screening
            };
        case DELETING_SCREENING_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                screeningSuccessfullyDeleted: false,
                loadingErrorMessage: action.error.message
            };
        case ADDING_PRICING:
            return {
                ...state,
                screening: {
                    ...state.screening,
                    pricings: {
                        ...state.screening.pricings,
                        [make_temporary_pricing_id()]: initialPricing(
                            action.names,
                            action.currency,
                            Object.keys(state.screening.pricings || {}).length + 1
                        )
                    }
                }
            };
        case DUPLICATING_PRICING: {
            const source = state.screening.pricings?.[action._id];
            if (!source)
                return state;
            const new_id = String(Date.now());
            const cloned = _.cloneDeep(source);
            if (!cloned.opaque)
                cloned.opaque = {};
            cloned.opaque.eshop_sort_weight = Object.keys(state.screening.pricings).length + 1;
            return {
                ...state,
                screening: {
                    ...state.screening,
                    pricings: {
                        ...state.screening.pricings,
                        [new_id]: cloned
                    }
                }
            };
        }
        case REMOVING_PRICING:
            const { [action._id]: toRemove, ...otherPricings } = state.screening.pricings;
            return {
                ...state,
                screening: {
                    ...state.screening,
                    pricings: otherPricings
                }
            };
        case TKTSYNCING_SCREENINGS:
            return {
                ...state,
                isTktsyncing: true,
                hasTktsyncingError: false
            };
        case TKTSYNCING_SCREENINGS_SUCCESS:
            return {
                ...state,
                isTktsyncing: false,
                hasTktsyncingError: false,
                imported: action.imported
            };
        case TKTSYNCING_SCREENINGS_FAILURE:
            return {
                ...state,
                isTktsyncing: false,
                hasTktsyncingError: true,
                tktsyncingErrorMessage: action.error.message
            };
        case UPDATING_SCREENING_PROPERTY:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case UPDATING_SCREENING_PROPERTY_SUCCESS: {
            const screening = { ...state.screening };
            switch (action.name) {
                case "title":
                case "description":
                case "pricings":
                case "note":
                case "cinema_hall":
                case "opaque":
                case "date":
                case "films":
                case "buckets":
                case "refs":
                    screening[action.name] = action.screening[action.name];
                    break;
                default:
                    break;
            }
            if (_.isInteger(action.name)) {
                screening.buckets = [...(state.screening.buckets || [])];
                screening.buckets[action.name] = action.screening.buckets[action.name];
            } else if (action.name === 'bucketlist_id') {
                screening.buckets = action.screening.buckets;
                screening.opaque = action.screening.opaque;
            } else if (action.name === 'pricinglist_id') {
                screening.pricings = action.screening.pricings;
                screening.opaque = action.screening.opaque;
            }

            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                screening,
            };
        }
        case UPDATING_SCREENING_PROPERTY_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case UPDATING_SCREENING_PRICINGS:
            return {
                ...state,
                screening: {
                    ...state.screening,
                    pricings: action.pricings
                }
            };
        case UPDATING_SCREENING_PLACE:
            return {
                ...state,
                screening: {
                    ...state.screening,
                    cinema_hall: action.place
                }
            };
        case UPDATING_FILTERS:
            return {
                ...state,
                filters: action.filters,
                hasLoadingError: false
            };
        case UPDATING_PRICING_KEY:
            const pricings = _.cloneDeep(state.screening?.pricings || {});
            if (pricings[action.values.newId])
                return { ...state }

            pricings[action.values.newId] = {
                ...pricings[action.values.oldId],
                ...(action.values.pricing_patch || {}),
            };
            delete pricings[action.values.oldId];
            return {
                ...state,
                screening: {
                    ...state.screening,
                    pricings
                }
            };

        default:
            return state;
    }
}

export default (state = initialState, action) => {
    return applyReducers(state, action, [linkedResourcesReducer, screeningsReducer]);
};

export const getScreeningsLinkedResources = linkedResourcesAction;

// Actions
function loadingScreenings(fetchScope, requestId) {
    return { type: LOADING_SCREENINGS, fetchScope: fetchScope ?? 'major', requestId };
}
function loadingScreeningsSuccess(screenings, requestId) {
    return { type: LOADING_SCREENINGS_SUCCESS, screenings: screenings, requestId };
}
function loadingScreeningsFailure(err, requestId) {
    return { type: LOADING_SCREENINGS_FAILURE, error: err, requestId };
}
export function loadScreenings(params, options = {}) {
    params = params || {};
    params.places_ids = (params.places_ids || []).join(',');
    return (dispatch) => {
        const requestId = v4();
        dispatch(loadingScreenings(options.fetchScope, requestId));
        Screenings.list(params)
            .then(data => {
                const screenings = data.screenings;
                dispatch(loadingScreeningsSuccess(screenings, requestId));
            })
            .catch(err => {
                console.log("err => ", err);
                dispatch(loadingScreeningsFailure(err, requestId))
            });
    }
}

function loadingScreening() { return { type: LOADING_SCREENING } }
function loadingScreeningSuccess(screening) { return { type: LOADING_SCREENING_SUCCESS, screening: screening } }
function loadingScreeningFailure(err) { return { type: LOADING_SCREENING_FAILURE, error: err } }
export function loadScreening(screeningId, fields) {
    if (screeningId)
        return (dispatch) => {
            dispatch(loadingScreening());
            Screenings.get(screeningId, fields)
                .then(data => {
                    const screening = data.screening;
                    dispatch(loadingScreeningSuccess(screening));
                })
                .catch(err => {
                    dispatch(loadingScreeningFailure(err))
                });
    }
}

function updatingScreening() { return { type: UPDATING_SCREENING } }
function updatingScreeningSuccess(screening) { return { type: UPDATING_SCREENING_SUCCESS, screening: screening } }
function updatingScreeningFailure(err) { return { type: UPDATING_SCREENING_FAILURE, error: err } }
export function updateScreening(screening) {
    return (dispatch) => {
        dispatch(updatingScreening());
        Screenings.update(screening)
            .then(data => {
                const screening = data.screening;
                dispatch(updatingScreeningSuccess(screening));
            })
            .catch(err => {
                dispatch(updatingScreeningFailure(err))
            });
    }
}

function creatingScreening() { return { type: CREATING_SCREENING } }
function creatingScreeningSuccess(screening) { return { type: CREATING_SCREENING_SUCCESS, screening: screening } }
function creatingScreeningFailure(err) { return { type: CREATING_SCREENING_FAILURE, error: err } }
export function startCreateScreening() { return { type: START_CREATING_SCREENING } }
export function createScreening(screening) {
    return (dispatch) => {
        dispatch(creatingScreening());
        Screenings.create(screening)
            .then(data => {
                const screening = data.screening;
                dispatch(creatingScreeningSuccess(screening));
                dispatch(loadingScreeningSuccess(screening));
            })
            .catch(err => {
                dispatch(creatingScreeningFailure(err))
            });
    }
}

function deletingScreening() { return { type: DELETING_SCREENING } }
function deletingScreeningSuccess() { return { type: DELETING_SCREENING_SUCCESS } }
function deletingScreeningFailure(err) { return { type: DELETING_SCREENING_FAILURE, error: err } }
export function deleteScreening(screeningId) {
    return (dispatch) => {
        dispatch(deletingScreening());
        Screenings.delete(screeningId)
            .then(() => {
                dispatch(deletingScreeningSuccess());
            })
            .catch(err => {
                dispatch(deletingScreeningFailure(err))
            });
    }
}

function addingPricing(names, currency) { return { type: ADDING_PRICING, names, currency } }
export function addPricing(names, currency) {
    return (dispatch, getState) => {
        dispatch(addingPricing(names, currency));
        // keepDirtyOnReinitialize does not inject new nested keys into the form.
        sync_screening_form_pricings(dispatch, getState);
    }
}

function duplicatingPricing(_id) { return { type: DUPLICATING_PRICING, _id } }
export function duplicatePricing(_id) {
    return (dispatch, getState) => {
        dispatch(duplicatingPricing(_id));
        sync_screening_form_pricings(dispatch, getState);
    }
}

function removingPricing(_id) { return { type: REMOVING_PRICING, _id } }
export function removePricing(_id) {
    return (dispatch, getState) => {
        dispatch(removingPricing(_id));
        sync_screening_form_pricings(dispatch, getState);
    }
}

function tktsyncingScreenings() { return { type: TKTSYNCING_SCREENINGS } }
function tktsyncingScreeningsSuccess(imported) { return { type: TKTSYNCING_SCREENINGS_SUCCESS, imported: imported } }
function tktsyncingScreeningsFailure(err) { return { type: TKTSYNCING_SCREENINGS_FAILURE, error: err } }
export function tktsyncScreenings() {
    return (dispatch) => {
        dispatch(tktsyncingScreenings());
        Screenings.tktsync()
            .then(data => {
                const imported = data.imported;
                dispatch(tktsyncingScreeningsSuccess(imported));
            })
            .catch(err => {
                console.log("err => ", err);
                dispatch(tktsyncingScreeningsFailure(err))
            });
    }
}

function updatingScreeningProperty() { return { type: UPDATING_SCREENING_PROPERTY } }
function updatingScreeningPropertySuccess(screening, name) {
    return { type: UPDATING_SCREENING_PROPERTY_SUCCESS, screening, name }
}
function updatingScreeningPropertyFailure(err) { return { type: UPDATING_SCREENING_PROPERTY_FAILURE, error: err } }
export function updateScreeningProperty(payload, name) {
    return (dispatch) => {
        dispatch(updatingScreeningProperty());
        return Screenings.updateScreeningProperty(payload)
            .then(data => {
                const screening = data.screening;
                dispatch(updatingScreeningPropertySuccess(screening, name));
                return data;
            })
            .catch(err => {
                dispatch(updatingScreeningPropertyFailure(err));
                return Promise.reject(err);
            });
    };
}

/**
 * Merge ScreeningForm bucket capacity inputs into server buckets and PUT once.
 * Kore forwards one engine PATCH per bucket; no engine change required.
 */
export function saveScreeningBucketCapacities() {
    return (dispatch, getState) => {
        const state = getState();
        const screening = state.screenings.screening;
        if (!screening?._id)
            return Promise.resolve();
        const form_buckets = getFormValues(SCREENING_FORM)(state)?.buckets;
        const merged_buckets = (screening.buckets || []).map((b, i) => {
            if (INTERNAL_SCREENING_BUCKET_IDS.includes(b._id))
                return b;
            const form_b = form_buckets?.[i];
            if (!form_b || form_b.total_capacity === undefined || form_b.total_capacity === '')
                return b;
            const n = parseInt(form_b.total_capacity, 10);
            if (!Number.isFinite(n))
                return b;
            return { ...b, total_capacity: n };
        });
        return dispatch(updateScreeningProperty({ _id: screening._id, buckets: merged_buckets }, 'buckets'));
    };
}

function updatingScreeningPricings(pricings) { return { type: UPDATING_SCREENING_PRICINGS, pricings } }
export function updateScreeningPricings(pricings) {
    return (dispatch) => {
        dispatch(updatingScreeningPricings(pricings));
    }
}

function updatingScreeningPlace(place) { return { type: UPDATING_SCREENING_PLACE, place } }
export function updateScreeningPlace(place) {
    return (dispatch) => {
        dispatch(updatingScreeningPlace(place));
    }
}

function updatingFilters(filters) { return { type: UPDATING_FILTERS, filters } }
export function updateFilters(filters) {
    return (dispatch, getState) => {
        dispatch(updatingFilters(filters));
        try {
            const { userId, instanceId } = resolveScreeningsFiltersSession(getState()?.auth?.user);
            const places_ids = getState()?.screenings?.filters?.places_ids;
            persistScreeningsFiltersUi(userId, instanceId, { places_ids });
        } catch (_e) {
            /* ignore */
        }
    }
}

function updatingPricingKey(values) { return { type: UPDATING_PRICING_KEY, values } }
export function updateScreeningPricingKey(values) {
    return (dispatch, getState) => {
        dispatch(updatingPricingKey(values));
        sync_screening_form_pricings(dispatch, getState, {
            oldId: values?.oldId,
            newId: values?.newId,
        });
    }
}
